/* eslint-disable @next/next/no-img-element */

import { getDuration } from "@/components/Visitors/utils/visitiorUtils";
import { CMSModal } from "@/context";
import DropDown from "@/designs/Dropdown";
import Input from "@/designs/Input";
import Select from "@/designs/Select";
import Table from "@/designs/Table";
import {
  EmployeeTimeline,
  GetEmployeesTimelineDocument,
  GetUsersDocument,
  useCreatedEmployeeTimelineDeviceMutation,
  useGetEmployeesTimelineQuery,
  User,
} from "@/graphql/generated/schema";
import { AnimatePresence, motion } from "framer-motion";
import {
  Laptop,
  Search,
  SlidersHorizontal,
  Tablet,
  Users2,
} from "lucide-react";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useSearchParams } from "next/navigation";
import toast from "react-hot-toast";
import { handleEmployeeExport } from "./EmployeeTimelineExport";
import ViewEmployeeTimeline from "./ViewEmployeeTimeline";
import DateRangePicker from "@/designs/DateRangePicker";
import Pagination from "@/designs/Pagination";
import { twMerge } from "tailwind-merge";
import VisitorStatusSelect from "@/components/Visitors/VisitorStatusSelect";
import OfficeLocation from "@/common/OfficeLocation";
import { useTranslation } from "react-i18next";

const EMPLOYEE_STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Signed In", value: "In" },
  { label: "Signed Out", value: "Out" },
  { label: "Remote", value: "Remote" },
];

const Timeline = () => {
  const { user, theme, selectedLocation } = useContext(CMSModal);
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const [editTimeline, setEditTimelIne] = useState(false);
  const [selecetdVisit, setSelectedVisit] = useState<EmployeeTimeline | any>();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 350);

  const [filters, setFilters] = useState({
    search: "",
    status: searchParams.get("status") || "",
    category: "",
    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    pageSize: 50,
    page: 1,
    sorted: {
      direction: "",
      columnId: "",
    },
    location: selectedLocation,
  });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, search: debouncedSearch }));
  }, [debouncedSearch]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, location: selectedLocation }));
  }, [selectedLocation]);

  useEffect(() => {
    if (searchParams.get("status")) setIsFilterOpen(true);
  }, []);
  const [selectedRows, setSelectedRows] = useState<any>([]);

  const { data, loading } = useGetEmployeesTimelineQuery({
    pollInterval: 5000,
    variables: {
      startDate: filters.startDate,
      endDate: filters.endDate,
      search: filters.search,
      limit: filters.pageSize,
      offset: (filters.page - 1) * filters.pageSize,
      sorted: filters.sorted,
      location: filters.location,
      signedType: filters.status || undefined,
    },
  });
  const employees = data?.getEmployeesTimeline?.employee || [];

  const totalCount = data?.getEmployeesTimeline?.count;
  const totalPages = Math.ceil((totalCount || 0) / filters.pageSize);

  const [createTimeline, { loading: createLoading }] =
    useCreatedEmployeeTimelineDeviceMutation({
      refetchQueries: [GetEmployeesTimelineDocument, GetUsersDocument],
    });
  const inVisitors = selectedRows.length
    ? selectedRows.filter((v: any) => v?.signedType === "In")
    : employees.filter((v: any) => v?.signedType === "In");
  const hasSelection = selectedRows.length > 0;
  const actionOptions = useMemo(() => {
    return [
      {
        id: "Export",
        label: `Export${!hasSelection ? " All" : ""} (${selectedRows.length || totalCount})`,
      },
      {
        id: "Out",
        label: `Sign Out${!hasSelection ? " All" : ""} (${inVisitors.length})`,
        disabled: inVisitors.length === 0,
      },
    ];
  }, [hasSelection, selectedRows.length, totalCount, inVisitors.length]);

  const handleActionSelect = async (action: any) => {
    switch (action.id) {
      case "Export":
        try {
          // ✅ 2. Define headers
          const headers = [
            "Employee Name",
            "Signed In",
            "Signed Out",
            "Duration",
          ];

          await handleEmployeeExport(
            headers,
            employees,
            selectedRows,
            "Employee_Timeline",
          );

          toast.success("Employee report exported successfully!");
        } catch (err) {
          console.error(err);
          toast.error("Failed to export Excel file");
        }
        break;

      case "Out":
        // TODO: Implement bulk sign out

        break;
      default:
        break;
    }
  };
  const onStatusChange = useCallback(
    async (row: EmployeeTimeline, newStatus: string) => {
      try {
        if (newStatus === "Out") {
          await createTimeline({
            variables: {
              input: {
                signedType: "Out",
                signedOut: new Date().toISOString(),
                signedIn: row?.signedIn,
                employee: row.employee?.id,
                _id: row?.id,
              },
            },
          });
        } else {
          await createTimeline({
            variables: {
              input: {
                signedType: newStatus,
                signedIn: new Date().toISOString(),
                employee: row.employee?.id,
              },
            },
          });
        }

        toast.success("Status updated successfully");
      } catch {
        toast.error("Failed to update status");
      }
    },
    [createTimeline],
  );
  const columns: any = [
    {
      id: "status",
      header: t("employees.columns.status"),
      accessor: "employee",
      render: (value: string, row: EmployeeTimeline) => (
        <div className="flex flex-col items-center w-16 py-1">
          <div
            className={twMerge(
              "w-15 h-15 rounded-full overflow-hidden border shadow-sm transition-colors",
              theme === "dark" ? "border-gray-800" : "border-gray-300",
            )}
          >
            <img
              src={row?.employee?.img || "/Avatar.svg"}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
          {row.signedType !== "Out" && (
            <VisitorStatusSelect
              value={row?.signedType || ""}
              onChange={(newStatus) => onStatusChange(row || "", newStatus)}
            />
          )}
        </div>
      ),
    },
    {
      id: "employee",
      header: t("employees.columns.employee"),
      accessor: "employee", // keep accessor for sorting/filtering if needed
      render: (value: User) =>
        `${value?.firstName || ""} ${value?.lastName || ""}`.trim() || "-",
      sortable: true,
    },
    {
      id: "location",
      header: t("employees.columns.location"),
      accessor: "location",
      render: (value: any) => value?.name || "-",
      sortable: true,
    },
    {
      id: "signedIn",
      header: t("employees.columns.signedIn"),
      accessor: "signedIn",
      render: (v: any, row: any) => {
        return (
          <>
            {row.signedIn ? (
              <div className="flex items-center gap-2">
                <p>{row?.signedInDevice === "Web" ? <Laptop /> : <Tablet />}</p>
                <p>
                  {row?.signedIn
                    ? moment(row?.signedIn).format("h:mma, MMM Do")
                    : "-"}
                </p>
              </div>
            ) : (
              "-"
            )}
          </>
        );
      },
      sortable: true,
    },
    {
      id: "signedOut",
      header: t("employees.columns.signedOut"),
      accessor: "signedOut",
      render: (v: any, row: any) => {
        return (
          <>
            {row.signedOut ? (
              <div className="flex items-center gap-2">
                <p>
                  {row?.signedOutDevice === "Web" ? <Laptop /> : <Tablet />}
                </p>
                <p>
                  {row?.signedOut
                    ? moment(row?.signedOut).format("h:mma, MMM Do")
                    : "-"}
                </p>
              </div>
            ) : (
              "-"
            )}
          </>
        );
      },
      sortable: true,
    },
    {
      id: "duration",
      header: t("employees.columns.duration"),
      render: (_: any, row: any) => getDuration(row.signedIn, row.signedOut),
    },
    {
      id: "message",
      header: t("employees.columns.message"),
      render: (_: any, row: any) =>
        `${row.statusMessage || ""} - ${
          row.returnTime ? moment(row.returnTime).format("HH:mm") : ""
        }`,
    },
  ];
  return (
    <div
      className={twMerge(
        "p-4 lg:p-10 space-y-4 transition-colors rounded",
        theme === "dark" ? "bg-[#191919]" : "bg-white",
      )}
    >
      <div className="flex flex-wrap items-center justify-between">
        <h2
          className={twMerge(
            "text-lg md:text-3xl font-semibold py-4",
            theme === "dark" ? "text-gray-100" : "text-[#3b3b3b]",
          )}
        >
          {t("employees.timeline.title")} <OfficeLocation />
        </h2>
        {
          <div className="border border-green-500 text-green-500 rounded-full p-1 min-w-32 text-center font-bold">
            {inVisitors.length}{" "}
            <span className="font-normal text-sm">
              {t("employees.timeline.inCount")}
            </span>
          </div>
        }
      </div>
      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <Input
            placeholder={t("employees.timeline.search")}
            icon={<Search size={18} />}
            value={searchInput}
            onChange={setSearchInput}
          />
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(start, end) =>
              setFilters({ ...filters, startDate: start, endDate: end })
            }
          />
          <button
            className="text-[#099ed1] flex items-center gap-1 font-bold"
            onClick={() => setIsFilterOpen((p) => !p)}
          >
            <SlidersHorizontal size={14} />
            {t("employees.timeline.filters")}
          </button>
        </div>

        <div className="flex items-center gap-8">
          <DropDown
            options={actionOptions}
            buttonLabel={t("employees.timeline.actions")}
            onSelect={(action) => handleActionSelect(action)}
          />
        </div>
      </div>

      {/* Conditional Filter Section */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.25, ease: "easeInOut" },
              opacity: { duration: 0.15, ease: "easeInOut" },
            }}
            className="overflow-hidden mb-4"
          >
            <div
              className={twMerge(
                "border p-4 flex gap-4 items-center",
                theme === "dark"
                  ? "bg-[#262727] border-gray-700"
                  : "bg-[#f5fbfd] border-[#c4e7f5]",
              )}
            >
              <Select
                label={t("employees.timeline.status")}
                value={filters.status}
                options={EMPLOYEE_STATUS_FILTERS}
                onChange={(value) =>
                  setFilters({ ...filters, status: value, page: 1 })
                }
                className="md:w-50"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Table
        data={employees}
        columns={columns}
        loading={loading || createLoading}
        setSelectedRow={(row: any) => {
          setSelectedVisit(row);
          setEditTimelIne(true);
        }}
        selectable={{
          active: true,
          selectedRows,
          setSelectedRows,
          rowIdKey: "_id", // ✅ Type-safe: "_id" exists in InvoiceRow
        }}
        emptyObject={{
          icon: <Users2 size={32} />,
          title: t("employees.timeline.noActivity"),
          description: t("employees.timeline.noActivityDesc"),
        }}
        selectedRow={selecetdVisit}
        setSorted={(value) =>
          setFilters((prev) => ({
            ...prev,
            sorted: value,
          }))
        }
      />
      {employees && employees.length > 0 && (
        <Pagination
          page={filters.page}
          totalPages={totalPages || 1}
          pageSize={filters.pageSize}
          onPageChange={(page) => setFilters({ ...filters, page })}
          onPageSizeChange={(pageSize) =>
            setFilters({ ...filters, pageSize, page: 1 })
          }
        />
      )}

      {editTimeline && (
        <ViewEmployeeTimeline
          isOpen={editTimeline}
          onClose={() => {
            setEditTimelIne(false);
            setSelectedVisit(null);
          }}
          timeline={selecetdVisit}
          allEmployees={employees}
          setTimeline={setSelectedVisit}
        />
      )}
    </div>
  );
};

export default Timeline;
